import { Dialog, DialogActions, DialogContent, DialogTitle, InputLabel, } from '@material-ui/core';
import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import _ from 'lodash';
import axios from 'axios';
import { useMsal } from '@azure/msal-react';
import AlertDialog from 'components/DialogComponent/AlertDialog';
import { formatDate } from 'helpers/DateFormatter';
import { APIEndpoints } from 'helpers/APILists';
import { MESSAGE_TYPE } from 'journeys/common/common.constants';
import TextInput from '../../elements/TextInput';
import { useStyles } from '../AdminForm.styled';
import { customSort, trackErrors } from '../helper';
import DropdownInput from '../../elements/DropdownInput';
import { AdminConstants } from 'journeys/portal/Admin/AdminScreen.constants';
import ModalFooterActions from '../../elements/ModalFooterActions';

const BarrierTypeMappingForm = ({ isOpen, title, onClose, rowData, onRecordSubmit, regionTypeOptions, workTypeOptions }) => {
    const classes = useStyles();
    const [createRowData, setCreateRowData] = useState(rowData);
    const [cancelAlert, setCancelAlert] = useState(false);
    const [regionTypeList, setRegionTypeList] = useState([]);
    const [regionExistError, setRegionExistError] = useState('');
    const { instance } = useMsal();
    const userName = instance.getActiveAccount();
    const submitButtonVisible = !_.isEqual(rowData, createRowData)
        && createRowData.RegionType
        && createRowData.WorkTypeCode
        && createRowData.WorkType
        && createRowData.CharacteristicDescription
        && createRowData.CharacteristicValue
        && createRowData.BarrierType
        && createRowData.Status
        && (createRowData.OrderIndex === 0 || createRowData.OrderIndex)
        && createRowData.DefaultBarrierType;
    const gridStyle = {
        display: 'grid',
        gridTemplateColumns: 'repeat(4, 1fr)',
        gap: '0 10px'
    };
    const workTypeCodeDropdownOptions = (() => {
        const match = workTypeOptions.find(x => x.WorkType === createRowData.WorkType);
        if (match?.WorkTypeCode) {
            return match.WorkTypeCode.split(',').map(code => code.trim());
        }
        return [];
    })();

    useEffect(() => {
        let isMounted = true;
        axios.get(APIEndpoints.ADMIN_GetBarrierTypeMappingDetails)
            .then((res) => {
                if (isMounted) {
                    setRegionTypeList(res.data);
                }
            })
            .catch((err) => {
                if (isMounted) {
                    trackErrors({
                        messageType: MESSAGE_TYPE.ERROR,
                        message: err.message,
                        exception: err
                    });
                }
            });
        return () => {
            isMounted = false;
        };
    }, []);

    const handleInputChange = ((e) => {
        setCreateRowData((prev) => (
            {
                ...prev,
                [e.target.name]: e.target.value
            }
        ));
    });

    const cancelButtonHandler = () => {
        if (_.isEqual(rowData, createRowData)) {
            onClose();
        } else {
            setCancelAlert(true);
        }
    };

    const handleUnsaveChanges = () => {
        setCancelAlert(false);
    };

    const resetButtonHandler = () => {
        setRegionExistError('');
        setCreateRowData(rowData);
    };

    const submitButtonHandler = () => {
        setRegionExistError('');
        const updatedRowData = {
            ...createRowData,
            DefaultBarrierType: createRowData.DefaultBarrierType === 'Yes' ? 1 : 0,
            OrderIndex: Number(createRowData.OrderIndex),
            ModifiedBy: userName?.name,
            LastModifiedDate: formatDate(new Date()),
            Status: createRowData.Status
        };
        const isExisting = regionTypeList.some(
            x => x.RegionType === updatedRowData.RegionType
                && x.WorkTypeCode === updatedRowData.WorkTypeCode
                && x.CharacteristicValue === updatedRowData.CharacteristicValue
        );
        const isEditing = !!rowData && (
            rowData.RegionType === updatedRowData.RegionType
            && rowData.WorkTypeCode === updatedRowData.WorkTypeCode
            && rowData.CharacteristicValue === updatedRowData.CharacteristicValue
        );

        if (!isEditing && isExisting) {
            setRegionExistError('(*A record with the same combination of Region Type, Work Type, and Characteristic Value already exists.)');
            return;
        }
        if (updatedRowData.RowId) {
            delete updatedRowData.id;
            updateRecord(updatedRowData);
        } else {
            updatedRowData.RowId = 0;
            updatedRowData.CreatedBy = userName?.name;
            updatedRowData.CreatedDate = formatDate(new Date());
            submitRecord(updatedRowData);
        }
    };
    const submitRecord = (dataRow) => {
        axios.post(APIEndpoints.ADMIN_AddBarrierTypeMappingDetails, dataRow)
            .then((res) => {
                onRecordSubmit(res.data.RowId);
                onClose();
            })
            .catch((err) => {
                trackErrors({
                    messageType: MESSAGE_TYPE.ERROR,
                    message: err.message,
                    exception: err
                });
            });
    };

    const updateRecord = (dataRow) => {
        axios.put(APIEndpoints.ADMIN_UpdateBarrierTypeMappingDetails, dataRow)
            .then((res) => {
                onRecordSubmit(res.data.RowId);
                onClose();
            })
            .catch((err) => {
                trackErrors({
                    messageType: MESSAGE_TYPE.ERROR,
                    message: err.message,
                    exception: err
                });
            });
    };

    return (
        <Dialog
            open={isOpen}
            scroll="paper"
            maxWidth="lg"
        >
            <DialogTitle id="scroll-dialog-title" classes={{ root: classes.headerRoot }} className={classes.header}>{title}</DialogTitle>
            {cancelAlert && (
                <AlertDialog
                    dialogMessage={AdminConstants.DIALOG_MESSAGE}
                    dialogOpenState={cancelAlert}
                    handleClose={() => handleUnsaveChanges()}
                    handleAlertFunction={onClose}
                />
            )}
            <DialogContent>
                <div style={gridStyle}>
                    <DropdownInput
                        name="RegionType"
                        title="BP Region Type"
                        value={createRowData.RegionType}
                        required
                        handleInputChange={handleInputChange}
                        options={customSort(_.uniq(regionTypeOptions.map(x => x.RegionType)))}
                    />
                    <DropdownInput
                        name="WorkType"
                        title="Work Type"
                        value={createRowData.WorkType}
                        required
                        handleInputChange={handleInputChange}
                        options={customSort(_.uniq(workTypeOptions.map(x => x.WorkType)))}
                    />
                    <DropdownInput
                        name="WorkTypeCode"
                        title="Work Type Code"
                        value={createRowData?.WorkTypeCode}
                        required
                        handleInputChange={handleInputChange}
                        options={customSort(_.uniq(workTypeCodeDropdownOptions))}
                    />
                    <TextInput
                        name="OrderIndex"
                        title="Order Index"
                        value={createRowData.OrderIndex}
                        handleInputChange={handleInputChange}
                        type="number"
                        required
                        isMultiline
                    />
                </div>
                <div style={gridStyle}>
                    <TextInput
                        name="CharacteristicDescription"
                        title="Characteristic Description (SAP PM)"
                        value={createRowData?.CharacteristicDescription}
                        required
                        handleInputChange={handleInputChange}
                        type="string"
                        maxLength="20"
                        isMultiline
                    />
                    <TextInput
                        name="CharacteristicValue"
                        title="Characteristic Value (SAP PM)"
                        value={createRowData?.CharacteristicValue}
                        required
                        handleInputChange={handleInputChange}
                        type="string"
                        maxLength="20"
                        isMultiline
                    />
                    <TextInput
                        name="BarrierType"
                        title="Barrier Type"
                        value={createRowData?.BarrierType}
                        required
                        handleInputChange={handleInputChange}
                        type="string"
                        maxLength="20"
                        isMultiline
                    />
                    <DropdownInput
                        name="DefaultBarrierType"
                        title="Default Barrier Type"
                        value={createRowData.DefaultBarrierType}
                        options={['Yes', 'No']}
                        handleInputChange={handleInputChange}
                        required
                    />
                </div>
                <div style={gridStyle}>
                    <DropdownInput
                        name="Status"
                        title="Status"
                        value={createRowData.Status}
                        options={['Active', 'Inactive']}
                        handleInputChange={handleInputChange}
                        required
                    />
                </div>
                <div style={gridStyle}>
                    <TextInput
                        name="CreatedBy"
                        title="Created By"
                        value={createRowData?.RegionId ? createRowData.CreatedBy : userName?.name}
                        disabled
                        handleInputChange={handleInputChange}
                    />
                    <TextInput
                        name="CreatedDate"
                        title="Created Date"
                        value={createRowData?.RegionId ? formatDate(new Date(createRowData.CreatedDate)) : formatDate(new Date())}
                        disabled
                        handleInputChange={handleInputChange}
                    />
                    <TextInput
                        name="ModifiedBy"
                        title="Last Modified By"
                        value={createRowData.ModifiedBy || userName?.name}
                        disabled
                        handleInputChange={handleInputChange}
                    />
                    <TextInput
                        name="LastModifiedDate"
                        title="Last Modified Date"
                        value={createRowData?.RegionId ? formatDate(new Date(createRowData.LastModifiedDate)) : formatDate(new Date())}
                        disabled
                        handleInputChange={handleInputChange}
                    />
                </div>
                <div style={{ display: 'grid', marginTop: '10px' }}>
                    <TextInput
                        name="Comments"
                        title="Comments"
                        value={createRowData.Comments}
                        handleInputChange={handleInputChange}
                        type="string"
                        isMultiline
                        width="1080px"
                    />
                </div>
                {regionExistError && <span style={{ fontSize: '11px', color: 'rgb(255, 0, 0)', fontWeight: 'bold', right: '10%', marginTop: '10px', display: 'inline-block' }}>{regionExistError}</span>}
            </DialogContent>
            <DialogActions style={{ marginRight: '15px', paddingBottom: '18px', paddingTop: '15px', justifyContent: 'space-between' }}>
                <div>
                    <InputLabel className={classes.inputLabel}>
                        <i> Note: If Region Type, Work Type, Work Type Code is not available in the list, please add a new record in corresponding LKUP tables.</i>
                    </InputLabel>
                    <InputLabel className={classes.inputLabelWithPadding}>
                        <i> Values for the fields with labels containing SAP PM are to be fetched from SAP PM.</i>
                    </InputLabel>
                </div>
                <ModalFooterActions
                    submitButtonVisible={submitButtonVisible}
                    submitButtonHandler={submitButtonHandler}
                    resetButtonHandler={resetButtonHandler}
                    cancelButtonHandler={cancelButtonHandler}
                />
            </DialogActions>
        </Dialog>
    );
};

export default BarrierTypeMappingForm;

BarrierTypeMappingForm.propTypes = {
    isOpen: PropTypes.bool,
    onClose: PropTypes.func,
    onRecordSubmit: PropTypes.func,
    regionTypeOptions: PropTypes.array,
    rowData: PropTypes.object,
    title: PropTypes.string,
    workTypeOptions: PropTypes.array
};
